import { getActiveThemeSlug, type ThemeSlug } from '@/themes/registry'

export type ModuleLoader = () => Promise<unknown>

type LoaderMap = Record<string, ModuleLoader>

// Vite requires literal globs. Gate: env non-empty and != default.
// Lookup filters strictly to the active slug when several theme folders exist.
const structuralOverrideLoaders: LoaderMap | undefined =
  import.meta.env.VITE_ACTIVE_THEME && import.meta.env.VITE_ACTIVE_THEME !== 'default'
    ? (import.meta.glob('../../themes/*/**/*.tsx') as LoaderMap)
    : undefined

export function normalizeSourcePath(sourcePath: string): string {
  return sourcePath.replace(/^@\/+/, '').replace(/^\/?src\/+/, '').replace(/^\/+/, '')
}

export function resolveOverridePath(sourcePath: string, themeSlug: ThemeSlug = getActiveThemeSlug()): string | undefined {
  if (themeSlug === 'default') {
    return undefined
  }

  const normalized = normalizeSourcePath(sourcePath)
  if (!normalized.startsWith('app/') && !normalized.startsWith('features/') && !normalized.startsWith('shared/components/')) {
    return undefined
  }

  return `/src/themes/${themeSlug}/${normalized}`
}

export function getThemeOverrideLoaders(themeSlug: ThemeSlug = getActiveThemeSlug()): LoaderMap {
  if (themeSlug === 'default' || structuralOverrideLoaders === undefined) {
    return {}
  }

  const filtered = Object.fromEntries(
    Object.entries(structuralOverrideLoaders).filter(([key]) =>
      key.replace(/\\/g, '/').includes(`/themes/${themeSlug}/`),
    ),
  )

  return normalizeThemeLoaderKeys(filtered, themeSlug)
}

export async function getThemedOverrideLoader(
  sourcePath: string,
  themeSlug: ThemeSlug = getActiveThemeSlug(),
): Promise<ModuleLoader | undefined> {
  const overridePath = resolveOverridePath(sourcePath, themeSlug)
  if (overridePath === undefined) {
    return undefined
  }

  return getThemeOverrideLoaders(themeSlug)[overridePath]
}

export function normalizeThemeLoaderKeys(loaders: LoaderMap, themeSlug: ThemeSlug): LoaderMap {
  return Object.fromEntries(
    Object.entries(loaders).map(([key, loader]) => [
      key.replace(`../../themes/${themeSlug}/`, `/src/themes/${themeSlug}/`),
      loader,
    ]),
  )
}
